import { computed, ref, unref } from 'vue'
import { SYNTH_DEFAULT_NOTE } from '../../stores/synthModel.js'

export function createSynthComposerNavigation(synthSource) {
  const viewMode = ref('song')
  const activeTrackIndex = ref(0)
  const activeTrack = computed(() => unref(synthSource)?.tracks?.[activeTrackIndex.value] ?? null)

  function showSong() {
    viewMode.value = 'song'
  }

  function selectTrack(index) {
    const synth = unref(synthSource)
    if (!Number.isInteger(index) || !synth?.tracks?.[index]) return false
    activeTrackIndex.value = index
    viewMode.value = 'track'
    return true
  }

  function reset() {
    viewMode.value = 'song'
    activeTrackIndex.value = 0
  }

  return { viewMode, activeTrackIndex, activeTrack, showSong, selectTrack, reset }
}

export function createTrackComposerSelection(initialPitch = SYNTH_DEFAULT_NOTE) {
  const selectedPitch = ref(initialPitch)
  const selectedEventIndex = ref(-1)

  function selectPitch(midi) {
    if (!Number.isInteger(midi) || midi < 0 || midi > 127) return false
    selectedPitch.value = midi
    return true
  }

  function selectEvent(index) {
    selectedEventIndex.value = Number.isInteger(index) && index >= 0 ? index : -1
  }

  function resetEventSelection() {
    selectedEventIndex.value = -1
  }

  return { selectedPitch, selectedEventIndex, selectPitch, selectEvent, resetEventSelection }
}

export function createComposerPlaybackState(synthSource) {
  const enabledTrackIndices = ref(new Set())

  function reset() {
    const synth = unref(synthSource)
    enabledTrackIndices.value = new Set((synth?.tracks ?? []).map((_, index) => index))
  }

  function isTrackEnabled(trackIndex) {
    return enabledTrackIndices.value.has(trackIndex)
  }

  function setTrackEnabled(trackIndex, enabled) {
    if (!Number.isInteger(trackIndex)) return false
    const next = new Set(enabledTrackIndices.value)
    if (enabled) next.add(trackIndex)
    else next.delete(trackIndex)
    enabledTrackIndices.value = next
    return true
  }

  function toggleTrackEnabled(trackIndex) {
    const nextEnabled = !enabledTrackIndices.value.has(trackIndex)
    return setTrackEnabled(trackIndex, nextEnabled)
  }

  function getEnabledTrackIndices() {
    return [...enabledTrackIndices.value].sort((a, b) => a - b)
  }

  reset()

  return {
    enabledTrackIndices,
    reset,
    isTrackEnabled,
    setTrackEnabled,
    toggleTrackEnabled,
    getEnabledTrackIndices,
  }
}
